import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Person} from '../types/Person';
import peopleService from '../services/peopleService';
import {useNavigate, useParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton, AdminForm, AdminFormSection, AdminInputText} from "../../../layout/admin";
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const PersonForm: React.FC = () => {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    surname: ''
  });
  const [person, setPerson] = useState<Person | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {id} = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  useEffect(() => {
    if (isEditMode && id) {
      fetchPerson(id);
    }
  }, [id, isEditMode]);

  const fetchPerson = async (personId: string): Promise<void> => {
    setLoading(true);
    try {
      const personData = await peopleService.getPerson(personId);
      setPerson(personData);
      setFormData({
        name: personData.name,
        surname: personData.surname
      });
    } catch (error) {
      console.error('Error fetching person:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    clearErrors();

    try {
      if (isEditMode && person) {
        const updatedPerson: Person = {
          id: person.id,
          name: formData.name,
          surname: formData.surname
        };
        await peopleService.updatePerson(updatedPerson);
      } else {
        await peopleService.createPerson({
          name: formData.name,
          surname: formData.surname
        });
      }
      navigate('/people');
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <div>{t('admin.common.loading')}</div>;
  }

  return (
    <>
      <AdminFormHeader
        title={isEditMode ? t('admin.people.title.edit') : t('admin.people.title.create')}
        subtitle={isEditMode ? t('admin.people.subtitle.edit') : t('admin.people.subtitle.create')}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection title={t('admin.common.sections.basicInfo')}>
              <AdminInputText
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                name="name"
                id="name"
                label={t('admin.people.fields.firstName')}
                required={true}
                error={errors.name}
              />

              <AdminInputText
                value={formData.surname}
                onChange={(e) => handleInputChange('surname', e.target.value)}
                name="surname"
                id="surname"
                label={t('admin.people.fields.surname')}
                required={true}
                error={errors.surname}
              />
            </AdminFormSection>
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate("/people")}
            />
            <AdminButton
              type="submit"
              label={person ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
              disabled={loading}
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default PersonForm;

